import React, { useState, useEffect, useCallback } from 'react';
import { purchaseOrdersAPI, vendorsAPI, projectsAPI, ffeAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Trash2, Pencil, Loader2, ShoppingBag, Send, Download, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { generatePurchaseOrderPDF } from '../lib/pdfExport';

const STATUSES = [
  { key: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  { key: 'sent', label: 'Sent', color: 'bg-blue-100 text-blue-700' },
  { key: 'acknowledged', label: 'Acknowledged', color: 'bg-violet-100 text-violet-700' },
  { key: 'partially_received', label: 'Partially Received', color: 'bg-amber-100 text-amber-700' },
  { key: 'received', label: 'Received', color: 'bg-emerald-100 text-emerald-700' },
  { key: 'matched', label: 'Matched', color: 'bg-teal-100 text-teal-700' },
  { key: 'cancelled', label: 'Cancelled', color: 'bg-rose-100 text-rose-700' },
];

const fmtINR = (n) => n ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';

export default function PurchaseOrders() {
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [ffeItems, setFfeItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [viewing, setViewing] = useState(null);

  const emptyForm = {
    vendor_id: '', project_id: '', notes: '', shipping_address: '', expected_delivery: '',
    items: [{ item_id: `i_${Date.now()}`, name: '', description: '', quantity: 1, unit_price: 0 }],
  };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [po, v, p, f] = await Promise.all([
        purchaseOrdersAPI.list(), vendorsAPI.list(), projectsAPI.list(), ffeAPI.list({}),
      ]);
      setOrders(po.data); setVendors(v.data); setProjects(p.data); setFfeItems(f.data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalOf = (items) => items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.quantity) || 1), 0);

  const addLine = () => setForm(f => ({ ...f, items: [...f.items, { item_id: `i_${Date.now()}`, name: '', description: '', quantity: 1, unit_price: 0 }] }));
  const updateLine = (idx, field, val) => setForm(f => {
    const items = [...f.items]; items[idx] = { ...items[idx], [field]: val }; return { ...f, items };
  });
  const removeLine = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const pickFFE = (idx, ffeId) => {
    const ffe = ffeItems.find(x => x.ffe_id === ffeId);
    if (!ffe) return;
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], name: ffe.name, description: ffe.description, unit_price: ffe.trade_price, ffe_id: ffe.ffe_id };
      return { ...f, items };
    });
  };

  const openCreate = () => { setForm(emptyForm); setEditing(null); setDialogOpen(true); };
  const openEdit = (po) => { setForm({ ...emptyForm, ...po, items: po.items?.length ? po.items : emptyForm.items }); setEditing(po); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vendor_id) { toast.error('Vendor required'); return; }
    if (!form.items.some(i => i.name?.trim())) { toast.error('At least one item'); return; }
    setFormLoading(true);
    try {
      if (editing) await purchaseOrdersAPI.update(editing.po_id, form);
      else await purchaseOrdersAPI.create(form);
      toast.success(editing ? 'Updated' : 'PO created');
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const changeStatus = async (po, newStatus) => {
    try { await purchaseOrdersAPI.updateStatus(po.po_id, { status: newStatus }); toast.success(`Status: ${newStatus}`); fetchData(); }
    catch (e) { toast.error('Failed'); }
  };

  const handleDelete = async (po) => {
    if (!window.confirm(`Delete ${po.po_number}?`)) return;
    try { await purchaseOrdersAPI.delete(po.po_id); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error('Failed'); }
  };

  const exportPDF = (po) => {
    try {
      const vendor = vendors.find(v => v.vendor_id === po.vendor_id);
      const project = projects.find(p => p.project_id === po.project_id);
      generatePurchaseOrderPDF(po, vendor, project);
    } catch (e) { toast.error('PDF export failed'); console.error(e); }
  };

  const sendPO = async (po) => {
    try {
      await purchaseOrdersAPI.updateStatus(po.po_id, { status: 'sent' });
      toast.success(`Marked ${po.po_number} as sent`);
      fetchData();
    } catch (e) { toast.error('Failed'); }
  };

  const totals = totalOf(form.items);

  return (
    <Layout>
      <div className="space-y-6" data-testid="purchase-orders-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light tracking-tight flex items-center gap-3">
              <ShoppingBag className="h-7 w-7 text-terra-500 text-orange-500" />Purchase Orders
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Raise POs to vendors, track delivery & match invoices</p>
          </div>
          <Button onClick={openCreate} data-testid="create-po-btn"><Plus className="h-4 w-4 mr-2" />New PO</Button>
        </div>

        {loading ? <div className="animate-pulse space-y-3">{[1,2].map(i => <div key={i} className="h-20 bg-muted/40 rounded-xl" />)}</div> :
          orders.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><ShoppingBag className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" /><p className="text-muted-foreground">No purchase orders yet</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {orders.map(po => {
                const st = STATUSES.find(s => s.key === po.status) || STATUSES[0];
                return (
                  <Card key={po.po_id} className="hover:shadow-sm transition-all" data-testid={`po-${po.po_id}`}>
                    <CardContent className="p-5">
                      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-mono text-sm font-medium">{po.po_number}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${st.color}`}>{st.label}</span>
                          </div>
                          <div className="text-sm mt-1">
                            <span className="text-muted-foreground">Vendor:</span> {po.vendor_name || '—'}
                            {po.project_name && <> · <span className="text-muted-foreground">Project:</span> {po.project_name}</>}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">{(po.items || []).length} line items · Expected {po.expected_delivery || '—'}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-medium">{fmtINR(po.total_amount)}</div>
                        </div>
                        <div className="flex gap-1 flex-wrap justify-end">
                          <Select value={po.status} onValueChange={v => changeStatus(po, v)}>
                            <SelectTrigger className="h-8 w-32 text-xs" data-testid={`po-status-${po.po_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{STATUSES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" onClick={() => setViewing(po)}><Eye className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" onClick={() => exportPDF(po)} data-testid={`export-pdf-${po.po_id}`}><Download className="h-3 w-3" /></Button>
                          {po.status === 'draft' && <Button size="sm" variant="outline" onClick={() => sendPO(po)} data-testid={`send-po-${po.po_id}`}><Send className="h-3 w-3" /></Button>}
                          <Button size="sm" variant="outline" onClick={() => openEdit(po)}><Pencil className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleDelete(po)} className="text-red-600"><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )
        }
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Purchase Order' : 'New Purchase Order'}</DialogTitle><DialogDescription>Commit to a vendor, track receipt, match against invoices</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Vendor *</Label>
                <Select value={form.vendor_id} onValueChange={v => setForm({...form, vendor_id: v})}>
                  <SelectTrigger data-testid="po-vendor-select"><SelectValue placeholder="Select vendor" /></SelectTrigger>
                  <SelectContent>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Project</Label>
                <Select value={form.project_id} onValueChange={v => setForm({...form, project_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Expected Delivery</Label><Input type="date" value={form.expected_delivery?.slice(0,10) || ''} onChange={e => setForm({...form, expected_delivery: e.target.value})} /></div>
              <div><Label>Shipping Address</Label><Input value={form.shipping_address} onChange={e => setForm({...form, shipping_address: e.target.value})} /></div>
              <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Line Items *</Label>
                <Button type="button" size="sm" variant="outline" onClick={addLine} data-testid="add-po-line-btn"><Plus className="h-3 w-3 mr-1" />Add Line</Button>
              </div>
              <div className="space-y-2">
                {form.items.map((it, idx) => (
                  <div key={it.item_id} className="p-3 border rounded space-y-2 bg-muted/20">
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                      <Input className="sm:col-span-4" placeholder="Item name" value={it.name} onChange={e => updateLine(idx, 'name', e.target.value)} />
                      <Select value={it.ffe_id || ''} onValueChange={v => pickFFE(idx, v)}>
                        <SelectTrigger className="sm:col-span-3"><SelectValue placeholder="From FF&E" /></SelectTrigger>
                        <SelectContent>{ffeItems.slice(0, 50).map(f => <SelectItem key={f.ffe_id} value={f.ffe_id}>{f.name}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="sm:col-span-1" type="number" placeholder="Qty" value={it.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)} />
                      <Input className="sm:col-span-3" type="number" placeholder="Unit price" value={it.unit_price} onChange={e => updateLine(idx, 'unit_price', e.target.value)} />
                      <Button type="button" variant="ghost" size="sm" className="sm:col-span-1 text-red-600" onClick={() => removeLine(idx)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                    <Input placeholder="Description (optional)" value={it.description || ''} onChange={e => updateLine(idx, 'description', e.target.value)} />
                    <div className="text-xs text-muted-foreground">Subtotal: {fmtINR((Number(it.unit_price) || 0) * (Number(it.quantity) || 1))}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end p-3 rounded bg-amber-50 text-sm">
              <div>Total: <span className="font-medium text-lg ml-2">{fmtINR(totals)}</span></div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-po-btn">{formLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={() => setViewing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewing && <>
            <DialogHeader><DialogTitle>{viewing.po_number}</DialogTitle><DialogDescription>Vendor: {viewing.vendor_name} · {viewing.project_name || 'No project'}</DialogDescription></DialogHeader>
            <div className="space-y-2">
              {viewing.items?.map((it, i) => (
                <div key={i} className="flex justify-between border-b py-2 text-sm">
                  <div>
                    <div className="font-medium">{it.name}</div>
                    {it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}
                    <div className="text-xs text-muted-foreground">{it.quantity} × {fmtINR(it.unit_price)}</div>
                  </div>
                  <div className="font-medium">{fmtINR((Number(it.unit_price) || 0) * (Number(it.quantity) || 1))}</div>
                </div>
              ))}
              <div className="flex justify-between pt-3 font-medium text-lg">
                <div>Total</div><div>{fmtINR(viewing.total_amount)}</div>
              </div>
              {viewing.notes && <div className="p-3 rounded bg-muted/30 text-sm">{viewing.notes}</div>}
            </div>
            <DialogFooter><Button onClick={() => exportPDF(viewing)}><Download className="h-4 w-4 mr-1" />PDF</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
